import { Color, Spacing } from '@persona-id/design-tokens';
import {
  CircleAlertIcon,
  EditBoxIcon,
  MailIcon,
  RotateCcwIcon,
} from '@persona-id/design-tokens/icons';
import dompurify from 'dompurify';
import { groupBy, orderBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import FormSendCaseEmail, { FormSendCaseEmailData } from 'dashboard/forms/FormSendCaseEmail';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { CaseMessage } from 'dashboard/models/case-message';
import { CaseEmailTemplate } from 'dashboard/models/case-template';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import {
  generateDefaultEmailBody,
  htmlContainsAnchor,
  sanitizeHtmlEmail,
  textOnly,
} from 'dashboard/modules/CaseEmail/helpers';
import { ModuleCaseEmail } from 'dashboard/modules/CaseEmail/model';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel } from 'lib/interfaces/api';

const Container = styled.div`
  display: grid;
  grid-template-columns: [left-column] 1fr [right-column] 2fr;
  height: inherit;
`;

const LeftColumn = styled.div`
  grid-column-start: left-column 1;
  height: inherit;
  border-right: 1px solid ${({ theme }) => theme.color.border.secondary};
  overflow-y: scroll;
`;

const RightColumn = styled.div`
  grid-column-start: right-column 1;
  display: flex;
  flex-direction: column;
  padding-top: ${Spacing[4]};
  padding-left: 26px;
  padding-right: ${Spacing[4]};
  margin-bottom: ${Spacing[4]};
  overflow-y: scroll;
`;

const Description = styled.div<{ isSelected: boolean }>`
  padding: ${Spacing[2]} ${Spacing[2]} ${Spacing[2]} ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.background.secondary};
  cursor: pointer;
  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.color.background.highlight};
    `}
`;

const Header = styled(Card.Title)`
  justify-content: space-between;
  padding-right: ${Spacing[4]};
  padding-top: ${Spacing[4]};
  padding-left: ${Spacing[5]};
`;

const StyledMailIcon = styled(MailIcon)`
  width: 40px;
  height: 40px;
`;

const StyledSticker = styled(Sticker)`
  padding: 0 2px;
`;

const StyledEmailPreviewBox = styled.div`
  display: grid;
  grid-template-areas:
    'subject subject date'
    'body body .';
`;

const SubjectPreview = styled(Paragraph)`
  grid-area: subject;
  color: ${COLORS.gray100};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DatePreview = styled(Paragraph)`
  grid-area: date;
  justify-self: right;
  align-self: center;
  padding-left: ${Spacing[1]};
`;

const EmailSummary = styled(Paragraph)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: ${Spacing[1]};
  grid-area: body;
`;

const EmptyEmailBody = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: ${({ theme }) => theme.color.background.secondary};
  height: 100%;
`;

const MessagesHeader = styled(Header)`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.color.background.primary};
`;

export interface CaseEmailProps {
  module: ModuleCaseEmail;
  mails: CaseMessage[];
  emailAddresses: string[];
  isPreview: boolean;
  inquiryTemplates: (InquiryTemplate | Template)[];
  users: User[];
  caseEmailTemplates: CaseEmailTemplate[];
  defaultCaseObjectRefId: string;
  handleCreateEmailDraft: ({
    to,
    from,
    body,
    threadId,
  }: {
    to: string[];
    from: string;
    body: string;
    threadId?: string;
  }) => Promise<void>;

  handleUpdateEmailDraft: (values: FormSendCaseEmailData) => Promise<void>;
  handleSendEmail: (values: FormSendCaseEmailData) => Promise<void>;
  handleDeleteEmail: (id: string) => Promise<void>;
}

const CaseEmail = ({
  module,
  mails,
  emailAddresses,
  inquiryTemplates,
  users,
  caseEmailTemplates,
  defaultCaseObjectRefId,
  isPreview = false,
  handleCreateEmailDraft,
  handleUpdateEmailDraft,
  handleSendEmail,
  handleDeleteEmail,
}: CaseEmailProps) => {
  const sortedMails = orderBy(
    (mail) => mail.attributes['updated-at'],
    'desc',
    mails,
  ) as CaseMessage[];
  const threads = groupBy((mail) => mail.attributes['thread-id'], sortedMails);

  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>(
    sortedMails[0]?.attributes['thread-id'],
  );

  // Update selected thread when threads are created or deleted
  useEffect(() => {
    setSelectedThreadId(Object.keys(threads)[0]);
  }, [Object.keys(threads).length]);

  const createEmailDraft = () => {
    handleCreateEmailDraft({
      to: emailAddresses.length > 0 ? [emailAddresses[0]] : [],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      from: module['default-from-email-address'],
      body: generateDefaultEmailBody(module['default-from-email-address']),
    });
  };

  return (
    <Container>
      <LeftColumn>
        <MessagesHeader>
          <Heading size='sm'>Messages</Heading>
          <Button
            id='case-emails__icon--compose-new-email'
            variant='inverse'
            width='icon'
            onClick={createEmailDraft}
            aria-label='Compose email'
            disabled={isPreview}
          >
            <EditBoxIcon />
          </Button>
        </MessagesHeader>
        <ThreadList
          threads={threads}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          selectedThreadId={selectedThreadId}
          setSelectedThreadId={setSelectedThreadId}
        />
      </LeftColumn>
      <RightColumn>
        {mails.length === 0 ? (
          <EmptyEmailBody>
            <StyledMailIcon />
            You have no new messages.
            <Spacer height={2}></Spacer>
            <Button
              id='case-emails__button--compose-new-email'
              variant='basic'
              width='auto'
              onClick={createEmailDraft}
            >
              Compose
            </Button>
          </EmptyEmailBody>
        ) : (
          <EmailBody
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            mails={threads?.[selectedThreadId] || Object.values(threads)[0]}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            defaultFromEmailAddress={module['default-from-email-address']}
            toEmailAddresses={emailAddresses}
            inquiryTemplates={inquiryTemplates}
            users={users}
            caseEmailTemplates={caseEmailTemplates}
            defaultCaseObjectRefId={defaultCaseObjectRefId}
            handleDeleteEmail={handleDeleteEmail}
            handleSendEmail={handleSendEmail}
            handleUpdateEmailDraft={handleUpdateEmailDraft}
            handleCreateEmailDraft={handleCreateEmailDraft}
          />
        )}
      </RightColumn>
    </Container>
  );
};

interface ThreadListProps {
  threads: Record<string, CaseMessage[]>;
  selectedThreadId: string;
  setSelectedThreadId: (threadId: string) => void;
}

const ThreadList = ({ threads, selectedThreadId, setSelectedThreadId }: ThreadListProps) => {
  return (
    <>
      {Object.keys(threads)
        .reduce(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          (threadList, threadId) => {
            const thread = threads[threadId];

            // show the first 'to' email address of a thread sorted by date sent in ascending order
            const { body, subject } = thread[thread.length - 1].attributes.mail;

            const description = (
              <Description
                key={threadId}
                id={threadId}
                onClick={() => {
                  setSelectedThreadId(threadId);
                }}
                isSelected={selectedThreadId === threadId}
              >
                <StyledEmailPreviewBox>
                  <SubjectPreview>
                    {!thread[thread.length - 1].attributes['sent-at'] && (
                      <StyledSticker>Draft</StyledSticker>
                    )}
                    &nbsp;
                    {subject || 'New message'}
                  </SubjectPreview>
                  <DatePreview size='xs'>
                    {DateTime.fromISO(thread[0].attributes['updated-at']).toRelative()}
                  </DatePreview>
                  <EmailSummary>{textOnly(body)}</EmailSummary>
                </StyledEmailPreviewBox>
              </Description>
            );
            // This ensures that any drafts are always at the top of the threads list
            if (thread[thread.length - 1].attributes['sent-at']) {
              return [threadList[0], [...threadList[1], description]];
            } else {
              return [[...threadList[0], description], threadList[1]];
            }
          },
          [[], []],
        )
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .flat()}
    </>
  );
};

const ThreadHeader = styled(HStack)`
  margin: ${Spacing[2]};
`;

const ReplyContainer = styled.div`
  padding: ${Spacing[2]};
  white-space: pre-wrap;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};

  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.color.background.primary};
`;

const ReplyButton = styled(Button)`
  margin-left: auto;
  width: fit-content;
`;

interface EmailBodyProps {
  mails: CaseMessage[];
  defaultFromEmailAddress: string;
  toEmailAddresses: string[];
  inquiryTemplates: (InquiryTemplate | Template)[];
  users: User[];
  caseEmailTemplates: CaseEmailTemplate[];
  defaultCaseObjectRefId: string;
  handleDeleteEmail: (id: string) => Promise<void>;
  handleSendEmail: (values: FormSendCaseEmailData) => Promise<void>;
  handleUpdateEmailDraft: (values: FormSendCaseEmailData) => Promise<void>;
  handleCreateEmailDraft: ({
    to,
    from,
    body,
    threadId,
  }: {
    to: string[];
    from: string;
    body: string;
    threadId?: string;
  }) => Promise<void>;
}

const EmailBody = ({
  mails,
  defaultFromEmailAddress,
  toEmailAddresses,
  inquiryTemplates,
  users,
  caseEmailTemplates,
  defaultCaseObjectRefId,
  handleDeleteEmail,
  handleSendEmail,
  handleUpdateEmailDraft,
  handleCreateEmailDraft,
}: EmailBodyProps) => {
  if (mails.length === 1 && !mails[0].attributes['sent-at']) {
    const draft = mails[0];

    return (
      <FormSendCaseEmail
        defaultValues={{
          id: draft.id,
          'from-email-address': draft.attributes.mail.from || defaultFromEmailAddress,
          'to-email-addresses':
            draft.attributes.mail.to || (toEmailAddresses.length > 0 ? [toEmailAddresses[0]] : []),
          'cc-email-addresses': draft.attributes.mail.cc || [],
          subject: draft.attributes.mail.subject || '',
          'body-text': draft.attributes.mail.body,
          'last-updated-at': draft.attributes['updated-at'],
          'related-objects': draft.attributes['related-objects'],
        }}
        emailAddresses={toEmailAddresses}
        inquiryTemplates={inquiryTemplates}
        caseEmailTemplates={caseEmailTemplates}
        defaultCaseObjectRefId={defaultCaseObjectRefId}
        handleDelete={() => handleDeleteEmail(draft.id)}
        sendEmail={handleSendEmail}
        handleUpdateEmailDraft={handleUpdateEmailDraft}
      />
    );
  } else {
    const { mail: threadOriginalEmail, 'thread-id': threadId } = mails[mails.length - 1].attributes;
    const hasDraft = !mails[0].attributes['sent-at'];
    const draft = mails[0];

    return (
      <>
        {hasDraft && (
          <ReplyContainer>
            <FormSendCaseEmail
              showFromField={false}
              defaultValues={{
                id: draft.id,
                'from-email-address': draft.attributes.mail.from,
                'to-email-addresses': draft.attributes.mail.to || [],
                subject:
                  draft.attributes.mail.subject ||
                  (mails[1].attributes.mail?.subject?.startsWith('Re:')
                    ? mails[1].attributes.mail.subject
                    : `Re: ${mails[1].attributes.mail.subject}`),
                'cc-email-addresses': draft.attributes.mail.cc || [],
                'body-text': draft.attributes.mail.body,
                'last-updated-at': draft.attributes['updated-at'],
                'related-objects': draft.attributes['related-objects'],
              }}
              emailAddresses={draft.attributes.mail.to || []}
              inquiryTemplates={inquiryTemplates}
              caseEmailTemplates={caseEmailTemplates}
              defaultCaseObjectRefId={defaultCaseObjectRefId}
              handleDelete={() => handleDeleteEmail(draft.id)}
              sendEmail={handleSendEmail}
              handleUpdateEmailDraft={handleUpdateEmailDraft}
            />
          </ReplyContainer>
        )}
        <ThreadHeader>
          <div
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(`<h3>${threadOriginalEmail.subject}</h3>`, {
                ALLOWED_TAGS: ['h3', 'b', 'em', 'i', 'strong', 'u'],
              }),
            }}
          />
          {!hasDraft && (
            <>
              <ReplyButton
                id='case-emails__button_reply'
                type='button'
                onClick={() =>
                  handleCreateEmailDraft({
                    to: threadOriginalEmail.to || [],
                    from: defaultFromEmailAddress,
                    body: generateDefaultEmailBody(defaultFromEmailAddress),
                    threadId,
                  })
                }
                prefixIcon={<RotateCcwIcon />}
                variant='basic'
                aria-label='Reply'
              >
                Reply
              </ReplyButton>
              <Spacer height={2} />
            </>
          )}
        </ThreadHeader>
        <EmailThread mails={mails} users={users} />
      </>
    );
  }
};

const CircleAlertContainer = styled.div`
  width: 16px;
  height: 16px;
  margin-top: 2px;
  margin-right: ${Spacing[2]};
`;

const WarningBannerContainer = styled.div`
  padding: ${Spacing[4]};
  background-color: ${Color.CoreYellow100};
  border-radius: ${Spacing[1]};
  margin-bottom: ${Spacing[2]};
  display: flex;
`;

const EmailContainer = styled.div`
  padding: ${Spacing[4]};
  white-space: pre-wrap;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
`;
interface EmailThreadProps {
  mails: CaseMessage[];
  users: User[];
}

const EmailThread = ({ mails, users }: EmailThreadProps) => {
  const [expandedEmails, setExpandedEmails] = useState<Record<string, boolean>>({});
  const canRenderCaseEmailLinks = useFeatureFlag('dashboard:render-case-email-links');

  const sanitize = (html?: string) => {
    if (canRenderCaseEmailLinks) {
      return sanitizeHtmlEmail(html ?? '');
    } else {
      return dompurify.sanitize(html, {
        ALLOWED_TAGS: ['b', 'em', 'i', 'strong', 'u', 'br', 'span'],
      });
    }
  };

  return (
    <>
      {mails.map((mail, i) => {
        if (!mail.attributes['sent-at']) {
          return <></>;
        }

        let sender = '';
        if (mail.attributes['sender-type'] === ApiModel.User) {
          const user = users.find((user) => user.id === mail.attributes['sender-id']);
          sender = `${userDisplayName(user)} <${mail.attributes.mail.from}>`;
        } else {
          sender = mail.attributes.mail.from;
        }
        const receiver = (mail.attributes.mail?.to || []).join(', ');
        const description = receiver ? `${sender} wrote to ${receiver}` : `${sender} wrote`;
        const sanitizedEmailBody = sanitize(mail.attributes.mail.body);
        const isInboundEmail = !mail.attributes['sender-type'];

        return (
          <EmailContainer key={i}>
            <Paragraph size='sm' color='primary'>
              {`On ${DateTime.fromISO(mail.attributes['sent-at']).toFormat(
                DATE_AND_TIME_FORMAT,
              )}, ${description}:`}
            </Paragraph>
            <Spacer height={2}></Spacer>
            {canRenderCaseEmailLinks &&
              htmlContainsAnchor(sanitizedEmailBody) &&
              isInboundEmail && (
                <WarningBannerContainer>
                  <CircleAlertContainer>
                    <CircleAlertIcon />
                  </CircleAlertContainer>
                  <Paragraph size='md' color='primary'>
                    Links are not scanned for malware. Exercise caution when viewing links from
                    unknown sources.
                  </Paragraph>
                </WarningBannerContainer>
              )}

            <div
              dangerouslySetInnerHTML={{
                __html: sanitizedEmailBody,
              }}
            />
            {mail.attributes.mail['hidden-body'] && (
              <>
                <Spacer height={1}></Spacer>
                <Button
                  id={`case-emails__EmailThread__expand-email-body-${mail.id}`}
                  onClick={() => {
                    setExpandedEmails({ ...expandedEmails, [mail.id]: !expandedEmails?.[mail.id] });
                  }}
                  variant='basic'
                  width='auto'
                  size='sm'
                >
                  {expandedEmails?.[mail.id] ? 'Hide original message' : 'Show original message'}
                </Button>
              </>
            )}

            {expandedEmails?.[mail.id] && (
              <>
                <Spacer height={1}></Spacer>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitize(mail.attributes.mail['hidden-body']),
                  }}
                />
              </>
            )}
          </EmailContainer>
        );
      })}
    </>
  );
};

export default CaseEmail;
